import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import AnimeCard from "@/components/feature/AnimeCard";
import FilterBar from "@/components/feature/FilterBar";
import EmptyState from "@/components/feature/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";
import type { AnimeFilter, SortKey } from "@/types/anime";
import { allAnime, filterAnime, getPopular, searchAnime } from "@/utils/anime";

const PAGE_SIZE = 12;

const hotKeywords = ["月下", "悬疑", "校园", "成人向", "治愈", "奇幻"];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("query") || "";
  const [input, setInput] = useState(query);
  const [visible, setVisible] = useState(PAGE_SIZE);

  const filter: AnimeFilter = {
    category: searchParams.get("category") || "all",
    region: searchParams.get("region") || "all",
    year: searchParams.get("year") || "all",
    status: searchParams.get("status") || "all",
    sort: (searchParams.get("sort") as SortKey) || "updated",
  };

  const matched = useMemo(() => searchAnime(allAnime, query), [query]);
  const results = useMemo(() => filterAnime(matched, filter), [query, searchParams]);
  const shown = results.slice(0, visible);
  const recommend = useMemo(() => getPopular(6), []);

  usePageMeta({
    title: query ? `“${query}” 搜索结果 - 色情动漫` : "搜索作品 - 色情动漫",
    description: query
      ? `在色情动漫站内搜索“${query}”，共找到 ${matched.length} 部相关成人向动画作品，可按题材、地区、年份与状态进一步筛选。`
      : "输入作品标题或标签，在色情动漫的作品库中快速检索成人向动画作品资料。",
    keywords: `色情动漫,动漫搜索,${query || "动漫检索"},动漫作品,成人动漫`,
  });

  useEffect(() => {
    setInput(query);
    setVisible(PAGE_SIZE);
  }, [query]);

  useEffect(() => {
    if (input.trim() === query) return;
    const timer = window.setTimeout(() => {
      const next = new URLSearchParams();
      if (input.trim()) next.set("query", input.trim());
      setSearchParams(next, { replace: true });
    }, 550);
    return () => window.clearTimeout(timer);
  }, [input]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = new URLSearchParams();
    if (input.trim()) next.set("query", input.trim());
    setSearchParams(next);
  };

  const updateFilter = (patch: Partial<AnimeFilter>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (!value || value === "all" || (key === "sort" && value === "updated")) {
        next.delete(key);
      } else {
        next.set(key, String(value));
      }
    });
    setSearchParams(next, { replace: true });
  };

  const resetFilter = () => {
    const next = new URLSearchParams();
    if (query) next.set("query", query);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />

      <main className="flex-1 w-full">
        <section className="w-full border-b border-background-200/70 bg-background-100/60">
          <div className="w-full px-4 md:px-8 lg:px-12 pt-8 pb-10">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="hover:text-accent-400 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-accent-400">搜索结果</span>
            </nav>

            <h1 className="mt-5 font-heading text-2xl md:text-4xl text-foreground-950 tracking-wide">
              {query ? "搜索结果" : "搜索作品"}
            </h1>
            <p className="mt-2 text-sm text-foreground-500">
              {query ? (
                <>
                  关键词 <span className="text-accent-400">“{query}”</span> · 在标题、原名与标签中共匹配到{" "}
                  <span className="text-foreground-900">{matched.length}</span> 部作品
                </>
              ) : (
                "输入作品标题、原名或标签关键词，例如「月下」「悬疑」「治愈」。"
              )}
            </p>

            <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-2xl">
              <div className="flex items-center gap-2 bg-background-50 border border-background-300/70 rounded-md px-3 h-11 flex-1 min-w-0 focus-within:border-accent-600 transition-colors">
                <i className="ri-search-2-line text-accent-500 text-lg" />
                <input
                  id="site-search-input"
                  name="query"
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  placeholder="搜索作品标题或标签"
                  className="flex-1 min-w-0 bg-transparent outline-none text-sm text-foreground-900 placeholder:text-foreground-400"
                />
              </div>
              <button
                type="submit"
                className="h-11 px-7 rounded-md bg-primary-600 hover:bg-primary-500 text-foreground-950 text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                搜索
              </button>
            </form>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500">试试</span>
              {hotKeywords.map((keyword) => (
                <Link
                  key={keyword}
                  to={`/search?query=${encodeURIComponent(keyword)}`}
                  className="h-7 px-2.5 rounded-full border border-background-300/70 text-xs text-foreground-600 hover:text-accent-400 hover:border-accent-600/60 transition-colors flex items-center"
                >
                  {keyword}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 lg:px-12 py-8 md:py-10">
          {query ? (
            <>
              <FilterBar filter={filter} onChange={updateFilter} onReset={resetFilter} total={results.length} />
              <div className="mt-8">
                {results.length === 0 ? (
                  <EmptyState
                    title="没有找到相关作品"
                    description={`没有与“${query}”匹配的作品。可以尝试更短的关键词、换个标签，或者从下方热门推荐开始浏览。`}
                    onReset={resetFilter}
                    resetLabel="清空筛选条件"
                    icon="ri-search-eye-line"
                  />
                ) : (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-7">
                      {shown.map((item) => (
                        <AnimeCard key={item.id} anime={item} />
                      ))}
                    </div>
                    <div className="mt-10 flex flex-col items-center gap-3">
                      <p className="text-xs text-foreground-500">已显示 {shown.length} / {results.length} 部</p>
                      {visible < results.length ? (
                        <button
                          type="button"
                          onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                          className="h-11 px-8 rounded-md border border-accent-600/50 text-accent-400 hover:bg-accent-500/10 text-sm flex items-center gap-2 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          加载更多结果
                          <i className="ri-arrow-down-line" />
                        </button>
                      ) : (
                        <span className="text-xs text-foreground-400">已经到底了</span>
                      )}
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
              <div>
                <h2 className="text-sm text-foreground-900 tracking-wide mb-4">热门推荐</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-7">
                  {recommend.map((item) => (
                    <AnimeCard key={item.id} anime={item} />
                  ))}
                </div>
              </div>
              <aside className="rounded-lg border border-background-300/50 bg-background-100/60 p-5 h-fit">
                <h2 className="text-sm text-foreground-900 tracking-wide mb-3">搜索小提示</h2>
                <ul className="flex flex-col gap-2.5 text-xs text-foreground-500 leading-relaxed">
                  <li className="flex gap-2"><i className="ri-checkbox-circle-line text-accent-500 mt-0.5" />支持作品中文标题、原名与别名匹配</li>
                  <li className="flex gap-2"><i className="ri-checkbox-circle-line text-accent-500 mt-0.5" />支持标签搜索，如「悬疑」「治愈」</li>
                  <li className="flex gap-2"><i className="ri-checkbox-circle-line text-accent-500 mt-0.5" />搜索结果可继续筛选地区与年份</li>
                  <li className="flex gap-2"><i className="ri-checkbox-circle-line text-accent-500 mt-0.5" />搜索仅覆盖站内已收录的作品资料</li>
                </ul>
              </aside>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}